/**
 * Adaptive Self-Rescheduling Polling Loop.
 *
 * Runs an asynchronous task on a dynamic schedule re-evaluated after each tick.
 * Uses generation tokens to supersede prior active chains and prevent overlapping
 * runs when cadence changes or manual kicks occur.
 */

export const POLL_CADENCES = {
  BASE_LIVE_MS: 8000,
  BASE_POLLING_MS: 3000,
  SECONDARY_LIVE_MS: 15000,
} as const;

export function resolvePollingCadence(
  isPushLive: boolean,
  liveCadenceMs: number,
  fallbackCadenceMs: number,
): number {
  return isPushLive ? liveCadenceMs : fallbackCadenceMs;
}

export interface AdaptiveLoopOptions {
  setTimeoutFn?: (handler: () => void, timeout?: number) => any;
  clearTimeoutFn?: (id: any) => void;
}

export interface AdaptiveLoopHandle {
  start: () => void;
  stop: () => void;
  getGeneration: () => number;
}

/**
 * Creates an adaptive self-rescheduling polling loop.
 */
export function createAdaptiveLoop(
  fn: () => Promise<void> | void,
  intervalFn: () => number,
  options: AdaptiveLoopOptions = {},
): AdaptiveLoopHandle {
  const setTimer = options.setTimeoutFn || setTimeout;
  const clearTimer = options.clearTimeoutFn || clearTimeout;

  let gen = 0;
  let timer: any = null;

  function stop() {
    gen++;
    if (timer) {
      clearTimer(timer);
      timer = null;
    }
  }

  function start() {
    const g = ++gen;
    if (timer) {
      clearTimer(timer);
      timer = null;
    }

    const tick = async () => {
      if (g !== gen) return; // superseded by a newer start/stop
      try {
        await fn();
      } finally {
        if (g === gen) {
          timer = setTimer(tick, intervalFn());
        }
      }
    };

    timer = setTimer(tick, intervalFn());
  }

  return {
    start,
    stop,
    getGeneration: () => gen,
  };
}
